import { useState } from 'react';
import { useAiCodeCheckerMutation } from '../redux/apiSlice';

interface UseCodeAiProps {
  language: string;
}

export const useCodeAi = ({ language }: UseCodeAiProps) => {
  // State for debug functionality
  const [debugAiResponse, setDebugAiResponse] = useState<string | null>(null);
  const [debugAiLoading, setDebugAiLoading] = useState<boolean>(false);
  const [debugError, setDebugError] = useState<string | null>(null);

  // State for explain highlighted functionality
  const [explainHighlightedAiResponse, setExplainHighlightedAiResponse] =
    useState<string | null>(null);
  const [explainHighlightedAiLoading, setExplainHighlightedAiLoading] =
    useState<boolean>(false);
  const [explainHighlightedError, setExplainHighlightedError] = useState<
    string | null
  >(null);

  // RTK Query hook
  const [aiCodeCheckerMutation] = useAiCodeCheckerMutation();

  // Debug user code
  const debugUserCode = async (
    userCode: string,
    correctCode: string,
    prompt: string,
    expectedOutput: string,
  ) => {
    if (!userCode.trim()) {
      setDebugError(
        'Please enter your code before requesting debugging assistance.',
      );
      setDebugAiResponse(null);
      return;
    }

    setDebugAiLoading(true);
    setDebugError(null);
    setDebugAiResponse(null);

    try {
      // Prepare debug prompt
      let debugPrompt = '';

      // // Special handling for typing courses
      if (language === 'typing') {
        debugPrompt = `Compare my code to the correct version and follow the System rules.
        ..........
        STUDENT TYPING: ${userCode}
        ..........
        REFERENCE TEXT: ${correctCode}
        `;
      } else {
        debugPrompt = `Compare my code to the correct version and follow the System rules. 
        ..........
        STUDENT CODE: ${userCode}
        ..........
        REFERENCE CODE: ${correctCode}
        `;
      }

      const response = await aiCodeCheckerMutation({
        prompt: debugPrompt,
        used_for: 'debug_my_code',
        language: language,
      }).unwrap();
      setDebugAiResponse(response.response);
    } catch (error) {
      console.error('Error in AI debug:', error);
      setDebugError('Failed to get AI debugging assistance. Please try again.');
    } finally {
      setDebugAiLoading(false);
    }
  };

  // Explain highlighted code
  const explainHighlightedCode = async (
    highlightedCode: string,
    fullCode: string,
  ) => {
    if (!highlightedCode.trim()) {
      setExplainHighlightedError(
        'No code is highlighted. Please highlight a section of code first.',
      );
      setExplainHighlightedAiResponse(null);
      return;
    }

    setExplainHighlightedAiLoading(true);
    setExplainHighlightedError(null);
    setExplainHighlightedAiResponse(null);

    try {
      // Prepare explain prompt
      const explainPrompt = `Explain the HIGHLIGHTED code per the System rules.
        ..........
        HIGHLIGHTED CODE: ${highlightedCode}
        ..........
        FULL PROGRAM: ${fullCode}
       `;

      const response = await aiCodeCheckerMutation({
        prompt: explainPrompt,
        used_for: 'explain_highlighted_code',
        language,
      }).unwrap();
      setExplainHighlightedAiResponse(response.response);
    } catch (error) {
      console.error('Error in AI explanation:', error);
      setExplainHighlightedError(
        'Failed to get AI explanation. Please try again.',
      );
    } finally {
      setExplainHighlightedAiLoading(false);
    }
  };

  // Check if code is correct
  const checkCodeCorrectness = async (
    userCode: string,
    correctCode: string,
    expectedOutput?: string,
    providedOutput?: string,
  ) => {
    try {
      console.log('userCode:', userCode);
      console.log('correctCode:', correctCode);
      // Prepare correctness check prompt
      let correctnessPrompt = '';

      correctnessPrompt = `Act as a coding expert in ${language}. If student code matches the reference code reply with only the number 1 if it's wrong reply with a 0, I will paste student code first then ... and then paste the reference code, 
      STUDENT CODE: ${userCode}
      ..........
      REFERENCE CODE: ${correctCode}`;

      const response = await aiCodeCheckerMutation({
        prompt: correctnessPrompt,
        used_for: 'checkCode',
        language,
      }).unwrap();
      console.log('response:', response);
      // Parse response and return boolean
      const isCorrect = response.response.includes('1');
      return isCorrect;
    } catch (error) {
      console.error('Error checking code correctness:', error);
      return false;
    }
  };

  const resetCodeAi = () => {
    setDebugAiResponse(null);
    setDebugAiLoading(false);
    setDebugError(null);
    setExplainHighlightedAiResponse(null);
  };

  return {
    // Debug related
    debugAiResponse,
    debugAiLoading,
    debugError,
    debugUserCode,

    // Explain highlighted related
    explainHighlightedAiResponse,
    explainHighlightedAiLoading,
    explainHighlightedError,
    explainHighlightedCode,

    // Correctness check
    checkCodeCorrectness,

    // Reset code AI
    resetCodeAi,
  };
};
